import React, { useMemo, useState } from 'react';
import { ChevronRightIcon, ArrowRightIcon } from 'lucide-react';
import { FeaturedAppCard } from '../components/AppCard/FeaturedAppCard';
import { PopularAppCard } from '../components/AppCard/PopularAppCard';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { mockApps } from '../utils/mockData';
const categories = [{
  id: 'productivity',
  label: 'Productivity',
  icon: '⚡️'
}, {
  id: 'creative',
  label: 'Creative',
  icon: '🎨'
}, {
  id: 'chat',
  label: 'Chat',
  icon: '💬'
}, {
  id: 'code',
  label: 'Code',
  icon: '👨‍💻'
}, {
  id: 'image',
  label: 'Image',
  icon: '🖼️'
}, {
  id: 'writing',
  label: 'Writing',
  icon: '✍️'
}];
export const Store = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const filterParam = searchParams.get('filter');
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  // Filter apps based on sidebar filter and active category
  const filteredApps = useMemo(() => {
    let filtered = [...mockApps];
    if (activeCategory) {
      filtered = filtered.filter(app => app.category.toLowerCase().includes(activeCategory.toLowerCase()));
    }
    if (!filterParam) return filtered;
    switch (filterParam) {
      case 'popular':
        filtered.sort((a, b) => {
          const aDownloads = parseInt(a.downloads.replace(/[^0-9]/g, ''));
          const bDownloads = parseInt(b.downloads.replace(/[^0-9]/g, ''));
          return bDownloads - aDownloads;
        });
        break;
      case 'featured':
        filtered = filtered.filter(app => app.rating >= 4.7);
        break;
      case 'new':
        filtered.sort((a, b) => {
          return new Date(b.updated).getTime() - new Date(a.updated).getTime();
        });
        break;
      case 'image':
        filtered = filtered.filter(app => app.category.toLowerCase().includes('image') || app.category.toLowerCase().includes('art'));
        break;
      case 'chat':
        filtered = filtered.filter(app => app.category.toLowerCase().includes('chat') || app.category.toLowerCase().includes('conversation'));
        break;
      case 'code':
        filtered = filtered.filter(app => app.category.toLowerCase().includes('code') || app.category.toLowerCase().includes('development'));
        break;
    }
    return filtered;
  }, [filterParam, activeCategory]);
  const getSectionTitle = () => {
    if (activeCategory) {
      const cat = categories.find(c => c.id === activeCategory);
      return cat ? `${cat.label} Apps` : 'Apps';
    }
    switch (filterParam) {
      case 'popular':
        return 'Popular Apps';
      case 'featured':
        return 'Featured Apps';
      case 'new':
        return 'New & Updated Apps';
      case 'image':
        return 'Image Generation Apps';
      case 'chat':
        return 'Chat & Conversation Apps';
      case 'code':
        return 'Code Generation Apps';
      default:
        return 'All Apps';
    }
  };
  const getSectionDescription = () => {
    if (activeCategory) {
      return `Explore the best ${activeCategory} applications`;
    }
    switch (filterParam) {
      case 'popular':
        return 'Most downloaded apps';
      case 'featured':
        return 'Curated by our team';
      case 'new':
        return 'Recently added and updated';
      case 'image':
        return 'Create stunning visuals with AI';
      case 'chat':
        return 'Intelligent conversation partners';
      case 'code':
        return 'AI-powered development tools';
      default:
        return 'Discover amazing AI tools';
    }
  };
  // Get top apps for different sections
  const topRatedApps = useMemo(() => [...mockApps].sort((a, b) => b.rating - a.rating).slice(0, 3), []);
  const trendingApps = useMemo(() => [...mockApps].sort((a, b) => {
    const aDownloads = parseInt(a.downloads.replace(/[^0-9]/g, ''));
    const bDownloads = parseInt(b.downloads.replace(/[^0-9]/g, ''));
    return bDownloads - aDownloads;
  }).slice(0, 6), []);
  return <div className="min-h-screen bg-gray-50 pb-20" data-id="element-2441">
      {/* Sticky Category Pills */}
      <div className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-10" data-id="element-2442">
        <div className="max-w-7xl mx-auto px-4 py-3" data-id="element-2443">
          <div className="flex space-x-2 overflow-x-auto scrollbar-hide pb-1" data-id="element-2444">
            {categories.map(category => <button key={category.id} onClick={() => setActiveCategory(category.id === activeCategory ? null : category.id)} className={`flex items-center px-4 py-2 rounded-full border whitespace-nowrap text-sm font-medium transition-colors ${activeCategory === category.id ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-2445">
                <span className="mr-2" data-id="element-2446">{category.icon}</span>
                {category.label}
              </button>)}
          </div>
        </div>
      </div>

      <div className="px-4 py-10 space-y-12 max-w-7xl mx-auto" data-id="element-2447">
        {filterParam || activeCategory /* Filtered Section */ ? <section data-id="element-2448">
            <div className="mb-8" data-id="element-2449">
              <h2 className="text-2xl font-bold text-gray-900" data-id="element-2450">
                {getSectionTitle()}
              </h2>
              <p className="text-gray-500 mt-1" data-id="element-2451">{getSectionDescription()}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-2452">
              {filteredApps.map(app => <FeaturedAppCard key={app.id} {...app} data-id="element-2453" />)}
            </div>
            {filteredApps.length === 0 && <div className="text-center py-20 bg-white rounded-2xl border border-gray-200 mt-6" data-id="element-2454">
                <p className="text-gray-500 text-lg" data-id="element-2455">
                  No apps found for this filter.
                </p>
                <button onClick={() => {
            setActiveCategory(null);
            navigate('/');
          }} className="mt-4 text-indigo-600 font-medium hover:text-indigo-700" data-id="element-2456">
                  Clear filters
                </button>
              </div>}
          </section> : <>
            {/* Hero Section */}
            <section className="py-12 md:py-20 text-center max-w-3xl mx-auto" data-id="element-2457">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 tracking-tight mb-6" data-id="element-2458">
                Discover AI Apps That Transform Your Work
              </h1>
              <p className="text-lg md:text-xl text-gray-600 mb-8" data-id="element-2459">
                Explore curated collections of powerful AI tools designed to
                boost your productivity and creativity.
              </p>
              <button onClick={() => navigate('/apps')} className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gray-900 hover:bg-gray-800 transition-colors" data-id="element-2460">
                Explore All Apps
                <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-2461" />
              </button>
            </section>

            {/* Editor's Picks */}
            <section data-id="element-2462">
              <div className="flex items-center justify-between mb-6" data-id="element-2463">
                <h2 className="text-xl font-semibold text-gray-900" data-id="element-2464">
                  Editor's Picks
                </h2>
                <Link to="/featured" className="text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center transition-colors" data-id="element-2465">
                  See All <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2466" />
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2467">
                {topRatedApps.map(app => <FeaturedAppCard key={app.id} {...app} data-id="element-2468" />)}
              </div>
            </section>

            {/* Trending Now */}
            <section data-id="element-2469">
              <div className="flex items-center justify-between mb-6" data-id="element-2470">
                <h2 className="text-xl font-semibold text-gray-900" data-id="element-2471">
                  Trending Now
                </h2>
                <button onClick={() => navigate('/?filter=popular')} className="text-sm font-medium text-gray-600 hover:text-gray-900 flex items-center transition-colors" data-id="element-2472">
                  See All <ChevronRightIcon className="h-4 w-4 ml-1" data-id="element-2473" />
                </button>
              </div>
              <div className="flex overflow-x-auto pb-6 -mx-4 px-4 md:grid md:grid-cols-3 lg:grid-cols-6 md:overflow-visible md:pb-0 md:mx-0 md:px-0 gap-4 scrollbar-hide snap-x" data-id="element-2474">
                {trendingApps.map(app => <div key={app.id} className="w-[160px] flex-shrink-0 md:w-auto md:flex-shrink snap-start" data-id="element-2475">
                    <PopularAppCard id={app.id} icon={app.icon} iconBg={app.iconBg} title={app.title} category={app.category} rating={app.rating} data-id="element-2476" />
                  </div>)}
              </div>
            </section>

            {/* Create CTA */}
            <section className="pt-8" data-id="element-2477">
              <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-12 text-center max-w-4xl mx-auto shadow-sm" data-id="element-2478">
                <h2 className="text-2xl font-semibold text-gray-900 mb-3" data-id="element-2479">
                  Create Your Own AI App
                </h2>
                <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-2480">
                  Turn your prompts into powerful AI applications and share them
                  with the world. No coding required.
                </p>
                <button onClick={() => navigate('/create')} className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-900 bg-white hover:bg-gray-50 transition-colors shadow-sm" data-id="element-2481">
                  Start Creating
                </button>
              </div>
            </section>
          </>}
      </div>
    </div>;
};